import { useMemo } from "react";
import {
  ChevronRight,
  ChevronDown,
  FileText,
  Layers,
  Box,
  Shield,
  Link2,
  Database,
  Folder,
} from "lucide-react";
import { useVisualizerStore } from "./store";
import type { TreeNode } from "./types";
import { Dropdown } from "../../components/Dropdown";

const iconMap: Record<string, React.ElementType> = {
  root: Layers,
  metadata: FileText,
  imports: Link2,
  import: Link2,
  components: Box,
  component: Box,
  capabilities: Shield,
  capability: Shield,
  resources: Database,
  resource: Database,
};

function TreeItem({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  const {
    selectedNodeId,
    selectNode,
    expandedNodes,
    toggleNode,
    searchQuery,
    filterType,
  } = useVisualizerStore();

  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const Icon = iconMap[node.type] || Folder;

  // Filter: if searchQuery or filterType is set, check match
  const matchesSearch =
    !searchQuery ||
    node.label.toLowerCase().includes(searchQuery.toLowerCase());

  const matchesFilter =
    !filterType ||
    node.type !== "component" ||
    (node.data as Record<string, unknown>)?.type === filterType;

  // For container nodes, check if any child matches
  const childrenFiltered = useMemo(() => {
    if (!node.children) return [];
    return node.children.filter((child) => {
      const childLabel = child.label.toLowerCase();
      const matchS =
        !searchQuery || childLabel.includes(searchQuery.toLowerCase());
      const matchF =
        !filterType ||
        child.type !== "component" ||
        (child.data as Record<string, unknown>)?.type === filterType;
      return matchS && matchF;
    });
  }, [node.children, searchQuery, filterType]);

  // Hide leaf nodes that don't match
  if (!hasChildren && (!matchesSearch || !matchesFilter)) return null;
  // Hide container nodes with no matching children
  if (hasChildren && childrenFiltered.length === 0 && !matchesSearch)
    return null;

  return (
    <div>
      <button
        onClick={() => {
          selectNode(node.id);
          if (hasChildren) toggleNode(node.id);
        }}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-left text-sm transition-colors rounded-md group ${
          isSelected
            ? "dark:bg-white/[0.1] bg-[#0f172a] dark:text-white text-white"
            : "dark:text-white/70 text-[#374151] dark:hover:bg-white/[0.05] hover:bg-[#f1f5f9]"
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          isExpanded ? (
            <ChevronDown className="size-3.5 flex-shrink-0 opacity-60" />
          ) : (
            <ChevronRight className="size-3.5 flex-shrink-0 opacity-60" />
          )
        ) : (
          <span className="size-3.5 flex-shrink-0" />
        )}
        <Icon
          className={`size-3.5 flex-shrink-0 ${isSelected ? "text-white" : "dark:text-white/60 text-[#94a3b8]"}`}
        />
        <span className="truncate font-medium">{node.label}</span>
        {hasChildren && (
          <span
            className={`ml-auto text-[10px] font-bold ${isSelected ? "text-white/60" : "dark:text-white/60 text-[#94a3b8]"}`}
          >
            {childrenFiltered.length}
          </span>
        )}
      </button>

      {hasChildren && isExpanded && (
        <div>
          {childrenFiltered.map((child) => (
            <TreeItem key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ComponentTree() {
  const {
    tree,
    searchQuery,
    setSearchQuery,
    expandAll,
    collapseAll,
    filterType,
    setFilterType,
    components,
  } = useVisualizerStore();

  const componentTypes = useMemo(() => {
    const types = new Set(components.map((c) => c.type));
    return Array.from(types);
  }, [components]);

  if (!tree) {
    return (
      <div className="flex flex-col items-center justify-center h-full dark:text-white/60 text-[#94a3b8] p-6">
        <Layers className="size-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No data loaded</p>
        <p className="text-xs mt-1">Import an OSCAL JSON file to begin</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-border space-y-2">
        <input
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-border rounded-md outline-none focus:border-indigo-500 dark:focus:border-indigo-500 transition-colors bg-background text-foreground placeholder:d"
        />
        <div className="flex items-center gap-1">
          {componentTypes.length > 0 && (
            <Dropdown
              size="sm"
              variant="form"
              className="flex-1"
              value={filterType}
              onChange={(v) => setFilterType(v)}
              options={[
                { value: "", label: "All Types" },
                ...componentTypes.map((t) => ({ value: t, label: t })),
              ]}
            />
          )}
          <button
            onClick={expandAll}
            className="px-2 py-1 text-[10px] font-bold text-[#64748b] dark:text-white/60 hover:text-[#0f172a] dark:hover:text-white border border-[#e5e7eb] dark:border-white/[0.1] rounded transition-colors"
          >
            Expand
          </button>
          <button
            onClick={collapseAll}
            className="px-2 py-1 text-[10px] font-bold text-[#64748b] dark:text-white/60 hover:text-[#0f172a] dark:hover:text-white border border-[#e5e7eb] dark:border-white/[0.1] rounded transition-colors"
          >
            Collapse
          </button>
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        <TreeItem node={tree} />
      </div>
    </div>
  );
}
